import React, { Component } from 'react';
import { Card, CardBody, Button, Spinner } from 'reactstrap';
import { withRouter } from 'react-router';
import Login from '~/Usuario/Login';
import IngresaCuenta from '~/components/IngresaCuenta';
import { Global } from '~/App';
import API from '~/API';
import './PortalCompacto.css';

/**
 * Normaliza y valida la URL de destino usando query parameters estándar (?seccion=...&params)
 * evitando el uso de hashes (#) que provocan fallos de sintaxis en querySelector de CMSs (ej: Wix)
 * y garantizando la propagación segura de tokens de sesión entre iframes/dominios.
 */
export function formatUrlDestino(urlDestino, targetRouteOrHash = '', extraParams = {}) {
  if (!urlDestino) return '';
  let url = urlDestino.trim();

  // Validación estricta anti-XSS y protocolos no seguros
  if (/^(javascript|data|vbscript):/i.test(url)) {
    console.warn("Protocolo no permitido en url-destino:", url);
    return '';
  }

  // Quitar cualquier hash previo
  url = url.replace(/#.*$/, '');

  // Parsear ruta base y query params preexistentes en urlDestino
  let [base, existingQuery] = url.split('?');
  if (base.endsWith('/')) {
    base = base.slice(0, -1);
  }

  const queryMap = new URLSearchParams(existingQuery || '');

  // Procesar targetRouteOrHash (ej: '#/paga?empresa=COOPAGUA', '/paga', 'sucursal')
  let cleanRoute = (targetRouteOrHash || '').trim();
  cleanRoute = cleanRoute.replace(/^#\/?/, '').replace(/^\//, '');

  let routeName = '';
  if (cleanRoute) {
    const [pathPart, routeQueryPart] = cleanRoute.split('?');
    routeName = pathPart.replace(/^\//, '').toLowerCase();

    if (routeQueryPart) {
      const inlineParams = new URLSearchParams(routeQueryPart);
      for (const [key, val] of inlineParams.entries()) {
        queryMap.set(key, val);
      }
    }
  }

  if (routeName) {
    queryMap.set('seccion', routeName);
  }

  // Incorporar extraParams
  if (extraParams && typeof extraParams === 'object') {
    for (const [key, val] of Object.entries(extraParams)) {
      if (val !== null && val !== undefined && val !== '') {
        queryMap.set(key, val);
      }
    }
  }

  // Si hay sesión activa (JWT de usuario), propagarla como authToken para no colisionar con token de boleta
  if (API.Cookie && !queryMap.has('authToken')) {
    queryMap.set('authToken', API.Cookie);
  }

  const queryString = queryMap.toString();
  return queryString ? `${base}?${queryString}` : base;
}

class PortalCompacto extends Component {
  constructor(props) {
    super(props);
    const vista = props.vista || Global.vista || 'login';
    const isAnonBlocked = Global.isAnonBlocked ? Global.isAnonBlocked() : false;
    // Si la consulta anónima está bloqueada, forzar pestaña login
    const initialTab = (vista === 'pago' && !isAnonBlocked) ? 'pago' : 'login';

    this.state = {
      tabActivo: initialTab,
      cerrandoSesion: false
    };
  }

  cambiarTab = (tab) => {
    this.setState({ tabActivo: tab });
  };

  irASucursal = (e) => {
    if (e) e.preventDefault();
    const urlDestino = this.props.urlDestino || Global.urlDestino;
    if (urlDestino) {
      const dest = formatUrlDestino(urlDestino, 'sucursal');
      if (dest) window.location.href = dest;
    } else {
      this.props.history.push('/sucursal');
    }
  };

  cerrarSesion = async (e) => {
    if (e) e.preventDefault();
    this.setState({ cerrandoSesion: true });
    try {
      await API.Logout();
      Global.setServicios([]);
      Global.setUsuario(null);
    } catch {}
    this.setState({ cerrandoSesion: false });
  };

  handleConsultaPago = ({ tipo, payload }) => {
    const urlDestino = this.props.urlDestino || Global.urlDestino;
    if (urlDestino) {
      const dest = formatUrlDestino(urlDestino, 'paga', payload);
      if (dest) window.location.href = dest;
    } else {
      this.props.history.push({
        pathname: '/paga',
        state: payload
      });
    }
  };

  render() {
    const { Usuario } = this.props;
    const vista = this.props.vista || Global.vista || 'login';
    const isAnonBlocked = Global.isAnonBlocked ? Global.isAnonBlocked() : false;
    const { tabActivo, cerrandoSesion } = this.state;
    // Solo mostrar pestañas si la consulta anónima está permitida
    const mostrarTabs = (vista === 'tabs' || vista === 'ambas') && !isAnonBlocked;

    // 1. Sesión activa: mostrar tarjeta de bienvenida y accesos rápidos
    if (Usuario) {
      const nombreCompleto = `${Usuario.nombre || Usuario.Nombre || ''} ${Usuario.apellido || Usuario.Apellido || ''}`.trim() || 'Cliente';
      const email = Usuario.email || Usuario.Email || Usuario.userName || '';

      return (
        <div className="portal-compacto-container">
          <Card className="portal-compacto-card">
            <CardBody className="portal-compacto-session">
              <div className="avatar-circle">
                <i className="fa fa-user" />
              </div>
              <h5 className="font-weight-bold mb-1">¡Hola, {nombreCompleto}!</h5>
              {email && <p className="text-muted small mb-3">{email}</p>}

              <div className="px-2 mt-3">
                <Button color="primary" block size="lg" onClick={this.irASucursal}>
                  Ir a la Sucursal Virtual <i className="fa fa-arrow-right ml-2" />
                </Button>
                <Button color="link" block size="sm" className="text-muted mt-2" onClick={this.cerrarSesion} disabled={cerrandoSesion}>
                  {cerrandoSesion ? <Spinner size="sm" /> : 'Cerrar Sesión'}
                </Button>
              </div>
            </CardBody>
          </Card>
        </div>
      );
    }

    // 2. Sin sesión: vista login, pago o tabs
    return (
      <div className="portal-compacto-container">
        <Card className="portal-compacto-card">
          {mostrarTabs && (
            <div className="portal-compacto-tabs">
              <button
                type="button"
                className={`portal-compacto-tab-btn ${tabActivo === 'login' ? 'active' : ''}`}
                onClick={() => this.cambiarTab('login')}
              >
                <i className="fa fa-user-circle mr-1" /> Sucursal Virtual
              </button>
              <button
                type="button"
                className={`portal-compacto-tab-btn ${tabActivo === 'pago' ? 'active' : ''}`}
                onClick={() => this.cambiarTab('pago')}
              >
                <i className="fa fa-credit-card mr-1" /> Pago Rápido
              </button>
            </div>
          )}

          {!mostrarTabs && (
            <div className="portal-compacto-header text-center">
              <h5 className="mb-0 font-weight-bold text-dark">
                {(vista === 'pago' && !isAnonBlocked) ? 'Paga tu Cuenta' : 'Sucursal Virtual'}
              </h5>
            </div>
          )}

          <div className="portal-compacto-body">
            {(tabActivo === 'login' || (!mostrarTabs && (vista !== 'pago' || isAnonBlocked))) ? (
              <Login />
            ) : (
              <IngresaCuenta {...this.props} onConsultar={this.handleConsultaPago} />
            )}
          </div>
        </Card>
      </div>
    );
  }
}

export default withRouter(PortalCompacto);
